/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Modal,
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  Switch,
  Image,
  RefreshControl,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import NetInfo from '@react-native-community/netinfo';

// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  projects: `${API_BASE_URL}/api/data/projects`,
  vendors: `${API_BASE_URL}/api/data/vendors`,
  contractTemplates: `${API_BASE_URL}/api/data/contract_templates`,
  contracts: `${API_BASE_URL}/api/data/contracts`,
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// FIDIC conditions (mapped by template ID)
const FIDIC_CONDITIONS = {
  fidic_red: `
**FIDIC Red Book – Conditions of Contract for Construction**

1. **Notice of Claims (Clause 20.1)**
   - Contractor must give notice within 28 days of becoming aware of a claim.
   - Failure to do so discharges Employer from liability.

2. **Employer's Claims (Clause 2.5)**
   - Employer must give notice and particulars before withholding payment.

3. **Unforeseeable Physical Conditions (Clause 4.12)**
   - Contractor entitled to extension of time and cost if conditions were unforeseeable.

4. **Delay Damages (Clause 8.7)**
   - Liquidated damages for late completion, subject to prior notice.

5. **Payment & Retention (Clause 14)**
   - Employer may withhold estimated cost of outstanding work.
  `,
  fidic_yellow: `
**FIDIC Yellow Book – Plant & Design-Build**

1. **Design Responsibility**
   - Contractor responsible for design of the works.

2. **Taking‑Over Certificate**
   - Issued when works are complete and have passed tests on completion.

3. **Defects Notification Period**
   - Contractor must rectify defects notified by Employer.

4. **Variations**
   - Engineer may order variations; value determined by measurement or lump sum.
  `,
  fidic_silver: `
**FIDIC Silver Book – EPC/Turnkey Projects**

1. **Employer's Requirements**
   - Contractor responsible for fulfilling Employer's Requirements.

2. **Lump Sum Price**
   - Contract price is fixed, subject only to adjustments for changes.

3. **Time for Completion**
   - Contractor liable for delay damages if completion is late.

4. **Testing**
   - Works must pass specified tests before taking over.
  `,
  fidic_green: `
**FIDIC Green Book – Short Form of Contract**

1. **Simple Works**
   - Suitable for low‑value, repetitive, or short‑duration works.

2. **Contract Price**
   - May be lump sum or remeasurement.

3. **Payment**
   - Interim payments based on work completed.

4. **Disputes**
   - Adjudication by a single adjudicator.
  `,
};

// UK statutory rules (always applicable)
const UK_STATUTORY_RULES = `
**UK Statutory Requirements**

1. **Construction Act 1996 (as amended)**
   - Right to adjudication at any time.
   - Payment mechanism must include interim valuations and pay‑less notices.

2. **CDM Regulations 2015**
   - Appointment of Principal Designer and Principal Contractor.
   - Duty to manage health and safety risks.

3. **Building Safety Act 2022 (for higher‑risk buildings)**
   - Mandatory occurrence reporting system.
   - Golden Thread of information.
`;

// Key for storing local contracts in AsyncStorage (fallback)
const LOCAL_CONTRACTS_KEY = 'local_contracts';
const SEED_FLAG_KEY = 'db_seeded';

// Helper to safely extract array from API response (handles many formats)
const extractDataArray = (responseData) => {
  if (!responseData) return [];
  if (Array.isArray(responseData)) return responseData;
  if (responseData.data && Array.isArray(responseData.data)) return responseData.data;
  if (responseData.records && Array.isArray(responseData.records)) return responseData.records;
  if (responseData.items && Array.isArray(responseData.items)) return responseData.items;
  if (responseData.results && Array.isArray(responseData.results)) return responseData.results;
  // If it's an object with numeric keys, convert to array
  if (typeof responseData === 'object' && !Array.isArray(responseData)) {
    const values = Object.values(responseData);
    if (values.length && values.every(v => typeof v === 'object')) return values;
  }
  return [];
};

// Mock data for seeding and fallback (only used if API fails)
const MOCK_PROJECTS = [
  { id: 'proj1', name: 'Downtown Office Complex', location: 'London, UK', budget: 2500000 },
  { id: 'proj2', name: 'Riverside Apartments', location: 'Manchester, UK', budget: 1800000 },
  { id: 'proj3', name: 'Industrial Park Expansion', location: 'Birmingham, UK', budget: 3200000 },
  { id: 'proj4', name: 'Community Health Center', location: 'Glasgow, UK', budget: 950000 },
  { id: 'proj5', name: 'University Research Building', location: 'Edinburgh, UK', budget: 1650000 },
];

const MOCK_VENDORS = [
  { id: 'sub1', name: 'Elite Electrical Services', specialty: 'Electrical', rating: 4.8 },
  { id: 'sub2', name: 'Modern Plumbing Co.', specialty: 'Plumbing', rating: 4.6 },
  { id: 'sub3', name: 'Foundation Masters', specialty: 'Foundation', rating: 4.9 },
  { id: 'sub4', name: 'Steel Structure Specialists', specialty: 'Structural Steel', rating: 4.7 },
  { id: 'sub5', name: 'Premier HVAC Systems', specialty: 'HVAC', rating: 4.5 },
  { id: 'sub6', name: 'Quality Drywall & Finishing', specialty: 'Drywall', rating: 4.4 },
  { id: 'sub7', name: 'Precision Concrete Solutions', specialty: 'Concrete', rating: 4.8 },
  { id: 'sub8', name: 'Advanced Roofing Ltd', specialty: 'Roofing', rating: 4.6 },
];

const MOCK_TEMPLATES = [
  { id: 'temp1', name: 'Standard Construction Contract', description: 'General purpose construction contract' },
  { id: 'temp2', name: 'Specialized Services Agreement', description: 'For specialized technical services' },
  { id: 'temp3', name: 'Time & Materials Contract', description: 'Flexible contract based on time and materials' },
  { id: 'temp4', name: 'Fixed Price Agreement', description: 'Set price contract with defined deliverables' },
];

const CreateNewContractScreen = ({ navigation, route }) => {
  // State for form fields
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerMode, setDatePickerMode] = useState('date');
  const [activeDateField, setActiveDateField] = useState(null);

  // Data fetched from API
  const [projects, setProjects] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [refreshingVendors, setRefreshingVendors] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const [companyId, setCompanyId] = useState(null);

  // State for company logo
  const [logoUrl, setLogoUrl] = useState(null);

  // Contract basic details
  const [contractData, setContractData] = useState({
    projectId: '',
    projectName: '',
    subcontractorId: '',
    subcontractorName: '',
    contractType: '',
    contractTemplate: '',
    totalAmount: '',
    startDate: new Date(),
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 3)),
    description: '',
    termsAccepted: false,
    milestones: [
      {
        id: `ms-${Date.now()}`,
        title: 'Initial Payment',
        description: '',
        amount: '',
        dueDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
        completionCriteria: '',
      },
    ],
    paymentTerms: {
      paymentMethod: 'Smart Contract',
      currency: 'GBP',
      autoRelease: false,
      requireEvidence: true,
      approvalWorkflow: 'Single Approver',
    },
    roles: {
      employer: '',
      contractor: '',
      engineer: '',
    },
    conditions: '',
  });

  // Modal states
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showSubcontractorModal, setShowSubcontractorModal] = useState(false);
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [roleField, setRoleField] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch company ID on mount
  useEffect(() => {
    const getCompanyId = async () => {
      const id = await AsyncStorage.getItem('company_id');
      setCompanyId(id ? parseInt(id) : null);
    };
    getCompanyId();
  }, []);

  // Fetch projects from API or fallback to mock
  const fetchProjects = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.projects);
      const data = extractDataArray(response.data);
      console.log('Projects fetched:', data.length);
      if (data.length > 0) {
        setProjects(data);
      } else {
        console.log('No projects from API, using mock');
        setProjects(MOCK_PROJECTS);
      }
    } catch (error) {
      console.error('Error fetching projects, using mock:', error);
      setProjects(MOCK_PROJECTS);
    }
  };

  // Fetch vendors (subcontractors) from API or fallback to mock
  const fetchVendors = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.vendors);
      const data = extractDataArray(response.data);
      console.log('Vendors fetched:', data.length);
      if (data.length > 0) {
        // Transform API data to a consistent format for display
        const transformed = data.map(v => ({
          id: v.id,
          name: v.company_name || v.contact_person || 'Unknown',
          specialty: Array.isArray(v.specialization) 
            ? v.specialization.join(', ') 
            : v.specialization || 'General',
          rating: v.ratings?.overall || v.overall || 'N/A',
          type: v.type || 'vendor',
        }));
        setSubcontractors(transformed);
      } else {
        console.log('No vendors from API, using mock');
        setSubcontractors(MOCK_VENDORS);
      }
      return data;
    } catch (error) {
      console.error('Error fetching vendors, using mock:', error);
      setSubcontractors(MOCK_VENDORS);
      return [];
    }
  };

  // Fetch templates from API or fallback to mock
  const fetchTemplates = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.contractTemplates);
      const data = extractDataArray(response.data);
      console.log('Templates fetched:', data.length);
      if (data.length > 0) {
        setTemplates(data);
      } else {
        console.log('No templates from API, using mock');
        setTemplates(MOCK_TEMPLATES);
      }
    } catch (error) {
      console.error('Error fetching templates, using mock:', error);
      setTemplates(MOCK_TEMPLATES);
    }
  };

  // Seed database if empty (runs in background, doesn't block UI)
  const seedDatabaseIfNeeded = async () => {
    try {
      const seeded = await AsyncStorage.getItem(SEED_FLAG_KEY);
      if (seeded === 'true') return;

      const netState = await NetInfo.fetch();
      if (!netState.isConnected) return;

      // Seed projects if they are still using mock (i.e., no real data)
      if (projects === MOCK_PROJECTS) {
        console.log('Seeding projects...');
        for (const project of MOCK_PROJECTS) {
          await api.post(API_ENDPOINTS.projects, project).catch(e => console.warn('Project seed failed', e));
        }
      }

      // Seed vendors if they are still using mock
      if (subcontractors === MOCK_VENDORS) {
        console.log('Seeding vendors...');
        for (const vendor of MOCK_VENDORS) {
          await api.post(API_ENDPOINTS.vendors, vendor).catch(e => console.warn('Vendor seed failed', e));
        }
      }

      await AsyncStorage.setItem(SEED_FLAG_KEY, 'true');
    } catch (error) {
      console.error('Seeding error:', error);
    }
  };

  // Load all data on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setFetchingData(true);

      const netState = await NetInfo.fetch();
      if (!netState.isConnected) {
        setIsOffline(true);
        setProjects(MOCK_PROJECTS);
        setSubcontractors(MOCK_VENDORS);
        setTemplates(MOCK_TEMPLATES);
        setFetchingData(false);
        return;
      }

      try {
        await loadCompanyLogo();

        // Fetch all data in parallel (they will fallback to mock if needed)
        await Promise.all([fetchProjects(), fetchVendors(), fetchTemplates()]);

        // After fetching, seed in background (don't await)
        seedDatabaseIfNeeded();
      } catch (error) {
        console.error('Error loading initial data:', error);
        Alert.alert('Error', 'Failed to load necessary data. Please try again.');
      } finally {
        setFetchingData(false);
      }
    };

    loadInitialData();
  }, []);

  // Listen for template selection from SmartContractTemplates
  useEffect(() => {
    if (route.params?.selectedTemplate) {
      const { templateId, templateData } = route.params.selectedTemplate;
      let conditions = '';
      if (templateId.startsWith('fidic_')) {
        conditions = FIDIC_CONDITIONS[templateId] || '';
        conditions += '\n\n' + UK_STATUTORY_RULES;
      }
      setContractData(prev => ({
        ...prev,
        contractTemplate: templateId,
        contractType: templateData.name || 'Custom Template',
        description: templateData.description || '',
        conditions,
      }));
    }
  }, [route.params?.selectedTemplate]);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setLogoUrl(profile.logo || null);
      }

      const cid = await AsyncStorage.getItem('company_id');
      if (cid) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: parseInt(cid) },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setLogoUrl(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // Manual refresh for vendors (tries API again)
  const refreshVendors = async () => {
    setRefreshingVendors(true);
    await fetchVendors(); // will update state with API data or keep mock
    setRefreshingVendors(false);
  };

  // Helper function to format currency
  const formatCurrency = amount => {
    if (!amount) return '';
    return `£${parseFloat(amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Handle date change
  const handleDateChange = (event, selectedDate) => {
    setShowDatePicker(false);
    if (selectedDate) {
      if (activeDateField === 'startDate') {
        setContractData({ ...contractData, startDate: selectedDate });
      } else if (activeDateField === 'endDate') {
        setContractData({ ...contractData, endDate: selectedDate });
      } else if (activeDateField.startsWith('milestone-')) {
        const index = parseInt(activeDateField.split('-')[1]);
        const updatedMilestones = [...contractData.milestones];
        updatedMilestones[index].dueDate = selectedDate;
        setContractData({ ...contractData, milestones: updatedMilestones });
      }
    }
  };

  // Show date picker
  const showDatePickerFor = (field, index) => {
    if (field === 'milestone') {
      setActiveDateField(`milestone-${index}`);
    } else {
      setActiveDateField(field);
    }
    setDatePickerMode('date');
    setShowDatePicker(true);
  };

  // Format date for display
  const formatDate = date => {
    if (!date) return '';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Handle project selection
  const handleSelectProject = project => {
    setContractData({
      ...contractData,
      projectId: project.id,
      projectName: project.name,
    });
    setShowProjectModal(false);
  };

  // Handle subcontractor selection – uses the transformed fields
  const handleSelectSubcontractor = subcontractor => {
    setContractData({
      ...contractData,
      subcontractorId: subcontractor.id,
      subcontractorName: subcontractor.name, // now mapped from company_name
    });
    setShowSubcontractorModal(false);
  };

  // Handle template selection – navigate to template library
  const handleSelectTemplate = () => {
    navigation.navigate('SmartContractTemplates', {
      onTemplateSelect: (templateId, templateData) => {
        navigation.setParams({
          selectedTemplate: { templateId, templateData },
        });
      },
    });
  };

  // Handle role selection
  const handleSelectRole = (roleValue) => {
    setContractData({
      ...contractData,
      roles: {
        ...contractData.roles,
        [roleField]: roleValue,
      },
    });
    setShowRolePicker(false);
  };

  // Add milestone
  const handleAddMilestone = () => {
    const newMilestone = {
      id: `ms-${Date.now()}`,
      title: `Milestone ${contractData.milestones.length + 1}`,
      description: '',
      amount: '',
      dueDate: new Date(
        new Date().setMonth(
          new Date().getMonth() + contractData.milestones.length + 1
        )
      ),
      completionCriteria: '',
    };

    setContractData({
      ...contractData,
      milestones: [...contractData.milestones, newMilestone],
    });
  };

  // Remove milestone
  const handleRemoveMilestone = index => {
    if (contractData.milestones.length <= 1) {
      Alert.alert('Error', 'Contract must have at least one milestone');
      return;
    }

    const updatedMilestones = [...contractData.milestones];
    updatedMilestones.splice(index, 1);

    setContractData({
      ...contractData,
      milestones: updatedMilestones,
    });
  };

  // Update milestone field
  const updateMilestoneField = (index, field, value) => {
    const updatedMilestones = [...contractData.milestones];
    updatedMilestones[index][field] = value;

    setContractData({
      ...contractData,
      milestones: updatedMilestones,
    });
  };

  // Calculate total milestone amount
  const calculateTotalMilestoneAmount = () => {
    return contractData.milestones.reduce((total, milestone) => {
      const amount = parseFloat(milestone.amount) || 0;
      return total + amount;
    }, 0);
  };

  // Check if milestone amounts match total contract amount
  const checkMilestoneAmounts = () => {
    const totalMilestoneAmount = calculateTotalMilestoneAmount();
    const contractAmount = parseFloat(contractData.totalAmount) || 0;

    return Math.abs(totalMilestoneAmount - contractAmount) < 0.01;
  };

  // Validate current step
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (!contractData.projectId) {
          Alert.alert('Error', 'Please select a project');
          return false;
        }
        if (!contractData.subcontractorId) {
          Alert.alert('Error', 'Please select a subcontractor');
          return false;
        }
        if (!contractData.contractType) {
          Alert.alert('Error', 'Please select a contract type');
          return false;
        }
        if (!contractData.totalAmount) {
          Alert.alert('Error', 'Please enter the total contract amount');
          return false;
        }
        return true;

      case 2:
        if (!checkMilestoneAmounts()) {
          Alert.alert('Error', 'Total milestone amounts must equal the total contract amount');
          return false;
        }
        for (let i = 0; i < contractData.milestones.length; i++) {
          const milestone = contractData.milestones[i];
          if (!milestone.title) {
            Alert.alert('Error', `Please enter a title for milestone ${i + 1}`);
            return false;
          }
          if (!milestone.amount) {
            Alert.alert('Error', `Please enter an amount for milestone ${i + 1}`);
            return false;
          }
        }
        return true;

      case 3:
        if (!contractData.termsAccepted) {
          Alert.alert('Error', 'Please accept the terms and conditions');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNextStep = () => {
    if (validateCurrentStep()) {
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        handleCreateContract();
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigation.goBack();
    }
  };

  const handleCreateContract = async () => {
    if (!companyId) {
      Alert.alert('Error', 'Company ID not found. Please log in again.');
      return;
    }
    setIsLoading(true);

    try {
      const payload = {
        contract_name: contractData.contractType,
        contract_type: contractData.contractType,
        first_party_id: companyId,
        second_party_name: contractData.subcontractorName,
        effective_date: contractData.startDate.toISOString().split('T')[0],
        contract_value: parseFloat(contractData.totalAmount),
        currency: contractData.paymentTerms.currency,
        status: 'Draft',
        metadata: JSON.stringify({
          projectId: contractData.projectId,
          projectName: contractData.projectName,
          vendor_id: contractData.subcontractorId,
          template_id: contractData.contractTemplate,
          description: contractData.description,
          conditions: contractData.conditions,
          end_date: contractData.endDate.toISOString().split('T')[0],
          payment_terms: contractData.paymentTerms,
          roles: contractData.roles,
          milestones: contractData.milestones,
        }),
      };

      let response;
      try {
        response = await api.post(API_ENDPOINTS.contracts, payload);
      } catch (apiError) {
        console.warn('Database save failed, falling back to local storage', apiError);
        await saveContractLocally(payload);
        Alert.alert(
          'Success',
          'Contract saved locally (database unavailable).',
          [{ text: 'OK', onPress: () => navigation.goBack() }]
        );
        setIsLoading(false);
        return;
      }

      if (response.status === 201 || response.data?.id) {
        Alert.alert(
          'Success',
          'Contract created successfully in database!',
          [{ text: 'OK', onPress: () => navigation.goBack() }]
        );
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (error) {
      console.error('Error creating contract:', error);
      let message = 'Failed to create contract. Please try again.';
      if (error.response) {
        const status = error.response.status;
        if (status === 400) message = 'Invalid contract data. Please check your inputs.';
        else if (status === 401) message = 'Authentication failed. Please log in again.';
        else if (status === 403) message = 'You do not have permission to create contracts.';
        else if (status === 422) message = 'Validation error: ' + (error.response.data?.message || 'Please check your data.');
        else message = `Server error (${status}). Please try later.`;
      } else if (error.request) {
        message = 'No response from server. Check your internet connection.';
      }
      Alert.alert('Error', message);
    } finally {
      setIsLoading(false);
    }
  };

  const saveContractLocally = async (contractPayload) => {
    try {
      const existing = await AsyncStorage.getItem(LOCAL_CONTRACTS_KEY);
      const contracts = existing ? JSON.parse(existing) : [];
      const newContract = {
        ...contractPayload,
        id: `local_${Date.now()}`,
        createdAt: new Date().toISOString(),
      };
      contracts.push(newContract);
      await AsyncStorage.setItem(LOCAL_CONTRACTS_KEY, JSON.stringify(contracts));
    } catch (error) {
      console.error('Failed to save contract locally', error);
      throw error;
    }
  };

  // Render step indicators
  const renderStepIndicators = () => (
    <View style={styles.stepIndicatorContainer}>
      {[1, 2, 3].map(step => (
        <View key={step} style={styles.stepIndicatorWrapper}>
          <View
            style={[
              styles.stepIndicator,
              currentStep === step ? styles.activeStepIndicator : {},
              currentStep > step ? styles.completedStepIndicator : {},
            ]}
          >
            {currentStep > step ? (
              <Ionicons name="checkmark" size={16} color="#fff" />
            ) : (
              <Text style={currentStep === step ? styles.activeStepText : styles.stepText}>
                {step}
              </Text>
            )}
          </View>
          <Text style={styles.stepLabel}>
            <Text>{step === 1 ? 'Details' : step === 2 ? 'Milestones' : 'Review'}</Text>
          </Text>
        </View>
      ))}
    </View>
  );

  // Render basic details form (Step 1)
  const renderBasicDetailsForm = () => (
    <View style={styles.formContainer}>
      <Text style={styles.sectionTitle}>Contract Details</Text>

      {/* Project Selection */}
      <Text style={styles.inputLabel}>Project</Text>
      <TouchableOpacity
        style={styles.selectionInput}
        onPress={() => setShowProjectModal(true)}
      >
        <Text style={contractData.projectName ? styles.inputText : styles.placeholderText}>
          {contractData.projectName || 'Select Project'}
        </Text>
        <Ionicons name="chevron-down" size={20} color="#64748B" />
      </TouchableOpacity>

      {/* Subcontractor Selection */}
      <Text style={styles.inputLabel}>Subcontractor</Text>
      <TouchableOpacity
        style={styles.selectionInput}
        onPress={() => setShowSubcontractorModal(true)}
      >
        <Text style={contractData.subcontractorName ? styles.inputText : styles.placeholderText}>
          {contractData.subcontractorName || 'Select Subcontractor'}
        </Text>
        <Ionicons name="chevron-down" size={20} color="#64748B" />
      </TouchableOpacity>

      {/* Contract Type/Template */}
      <Text style={styles.inputLabel}>Contract Template</Text>
      <TouchableOpacity
        style={styles.selectionInput}
        onPress={handleSelectTemplate}
      >
        <Text style={contractData.contractType ? styles.inputText : styles.placeholderText}>
          {contractData.contractType || 'Select Contract Template'}
        </Text>
        <Ionicons name="chevron-down" size={20} color="#64748B" />
      </TouchableOpacity>

      {/* Contract Amount */}
      <Text style={styles.inputLabel}>Total Contract Amount (£)</Text>
      <TextInput
        style={styles.textInput}
        placeholder="Enter amount"
        keyboardType="numeric"
        value={contractData.totalAmount}
        onChangeText={text =>
          setContractData({ ...contractData, totalAmount: text.replace(/[^0-9.]/g, '') })
        }
      />

      {/* Contract Dates */}
      <View style={styles.dateContainer}>
        <View style={styles.dateField}>
          <Text style={styles.inputLabel}>Start Date</Text>
          <TouchableOpacity style={styles.dateInput} onPress={() => showDatePickerFor('startDate')}>
            <Text style={styles.dateText}>{formatDate(contractData.startDate)}</Text>
            <Ionicons name="calendar-outline" size={20} color="#64748B" />
          </TouchableOpacity>
        </View>
        <View style={styles.dateField}>
          <Text style={styles.inputLabel}>End Date</Text>
          <TouchableOpacity style={styles.dateInput} onPress={() => showDatePickerFor('endDate')}>
            <Text style={styles.dateText}>{formatDate(contractData.endDate)}</Text>
            <Ionicons name="calendar-outline" size={20} color="#64748B" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Contract Description (auto-filled from template) */}
      <Text style={styles.inputLabel}>Description</Text>
      <TextInput
        style={[styles.textInput, styles.textArea]}
        placeholder="Enter contract description"
        multiline
        numberOfLines={4}
        value={contractData.description}
        onChangeText={text => setContractData({ ...contractData, description: text })}
      />

      {/* Roles Section (FIDIC) */}
      <Text style={styles.sectionTitle}>Contract Roles (FIDIC)</Text>

      <Text style={styles.inputLabel}>Employer</Text>
      <TouchableOpacity
        style={styles.selectionInput}
        onPress={() => { setRoleField('employer'); setShowRolePicker(true); }}
      >
        <Text style={contractData.roles.employer ? styles.inputText : styles.placeholderText}>
          {contractData.roles.employer || 'Select Employer'}
        </Text>
        <Ionicons name="chevron-down" size={20} color="#64748B" />
      </TouchableOpacity>

      <Text style={styles.inputLabel}>Contractor</Text>
      <TouchableOpacity
        style={styles.selectionInput}
        onPress={() => { setRoleField('contractor'); setShowRolePicker(true); }}
      >
        <Text style={contractData.roles.contractor ? styles.inputText : styles.placeholderText}>
          {contractData.roles.contractor || 'Select Contractor'}
        </Text>
        <Ionicons name="chevron-down" size={20} color="#64748B" />
      </TouchableOpacity>

      <Text style={styles.inputLabel}>Engineer</Text>
      <TouchableOpacity
        style={styles.selectionInput}
        onPress={() => { setRoleField('engineer'); setShowRolePicker(true); }}
      >
        <Text style={contractData.roles.engineer ? styles.inputText : styles.placeholderText}>
          {contractData.roles.engineer || 'Select Engineer'}
        </Text>
        <Ionicons name="chevron-down" size={20} color="#64748B" />
      </TouchableOpacity>
    </View>
  );

  // Render milestones form (Step 2)
  const renderMilestonesForm = () => (
    <View style={styles.formContainer}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Milestones & Payments</Text>
        <TouchableOpacity style={styles.addButton} onPress={handleAddMilestone}>
          <Ionicons name="add" size={20} color="#fff" />
          <Text style={styles.addButtonText}>Add Milestone</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.milestoneSummary}>
        <Text style={styles.milestoneSummaryText}>
          Total Contract: {formatCurrency(contractData.totalAmount || 0)}
        </Text>
        <Text
          style={[
            styles.milestoneSummaryText,
            checkMilestoneAmounts() ? styles.matchingAmount : styles.mismatchAmount,
          ]}
        >
          Total Milestones: {formatCurrency(calculateTotalMilestoneAmount())}
        </Text>
      </View>

      {contractData.milestones.map((milestone, index) => (
        <View key={milestone.id} style={styles.milestoneCard}>
          <View style={styles.milestoneHeader}>
            <Text style={styles.milestoneNumber}>Milestone {index + 1}</Text>
            <TouchableOpacity style={styles.removeButton} onPress={() => handleRemoveMilestone(index)}>
              <Ionicons name="trash-outline" size={20} color="#EF4444" />
            </TouchableOpacity>
          </View>

          <Text style={styles.inputLabel}>Title</Text>
          <TextInput
            style={styles.textInput}
            placeholder="Milestone title"
            value={milestone.title}
            onChangeText={text => updateMilestoneField(index, 'title', text)}
          />

          <Text style={styles.inputLabel}>Amount (£)</Text>
          <TextInput
            style={styles.textInput}
            placeholder="Enter amount"
            keyboardType="numeric"
            value={milestone.amount}
            onChangeText={text => updateMilestoneField(index, 'amount', text.replace(/[^0-9.]/g, ''))}
          />

          <Text style={styles.inputLabel}>Due Date</Text>
          <TouchableOpacity style={styles.dateInput} onPress={() => showDatePickerFor('milestone', index)}>
            <Text style={styles.dateText}>{formatDate(milestone.dueDate)}</Text>
            <Ionicons name="calendar-outline" size={20} color="#64748B" />
          </TouchableOpacity>

          <Text style={styles.inputLabel}>Completion Criteria</Text>
          <TextInput
            style={[styles.textInput, styles.textArea]}
            placeholder="Describe what constitutes completion of this milestone"
            multiline
            numberOfLines={3}
            value={milestone.completionCriteria}
            onChangeText={text => updateMilestoneField(index, 'completionCriteria', text)}
          />
        </View>
      ))}
    </View>
  );

  // Render payment terms and review (Step 3)
  const renderPaymentTermsAndReview = () => (
    <View style={styles.formContainer}>
      <Text style={styles.sectionTitle}>Payment Terms</Text>

      <View style={styles.settingRow}>
        <Text style={styles.settingLabel}>Payment Method</Text>
        <View style={styles.settingValue}>
          <Text style={styles.settingText}>{contractData.paymentTerms.paymentMethod}</Text>
        </View>
      </View>

      <View style={styles.settingRow}>
        <Text style={styles.settingLabel}>Currency</Text>
        <View style={styles.settingValue}>
          <Text style={styles.settingText}>{contractData.paymentTerms.currency}</Text>
        </View>
      </View>

      <View style={styles.settingRow}>
        <Text style={styles.settingLabel}>Auto-Release Payments</Text>
        <Switch
          value={contractData.paymentTerms.autoRelease}
          onValueChange={value =>
            setContractData({
              ...contractData,
              paymentTerms: { ...contractData.paymentTerms, autoRelease: value },
            })
          }
          trackColor={{ false: '#CBD5E1', true: '#0e4e96' }}
          thumbColor="#fff"
        />
      </View>

      <View style={styles.settingRow}>
        <Text style={styles.settingLabel}>Require Completion Evidence</Text>
        <Switch
          value={contractData.paymentTerms.requireEvidence}
          onValueChange={value =>
            setContractData({
              ...contractData,
              paymentTerms: { ...contractData.paymentTerms, requireEvidence: value },
            })
          }
          trackColor={{ false: '#CBD5E1', true: '#0e4e96' }}
          thumbColor="#fff"
        />
      </View>

      <Text style={styles.sectionTitle}>Contract Summary</Text>

      <View style={styles.summaryCard}>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Project:</Text>
          <Text style={styles.summaryValue}>{contractData.projectName}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Subcontractor:</Text>
          <Text style={styles.summaryValue}>{contractData.subcontractorName}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Contract Type:</Text>
          <Text style={styles.summaryValue}>{contractData.contractType}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Total Amount:</Text>
          <Text style={styles.summaryValue}>{formatCurrency(contractData.totalAmount || 0)}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Duration:</Text>
          <Text style={styles.summaryValue}>
            {formatDate(contractData.startDate)} - {formatDate(contractData.endDate)}
          </Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Milestones:</Text>
          <Text style={styles.summaryValue}>{contractData.milestones.length}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Employer:</Text>
          <Text style={styles.summaryValue}>{contractData.roles.employer || 'Not specified'}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Contractor:</Text>
          <Text style={styles.summaryValue}>{contractData.roles.contractor || 'Not specified'}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Engineer:</Text>
          <Text style={styles.summaryValue}>{contractData.roles.engineer || 'Not specified'}</Text>
        </View>
      </View>

      {/* Contract Conditions (FIDIC + UK) */}
      {contractData.conditions ? (
        <View style={styles.conditionsCard}>
          <Text style={styles.sectionTitle}>Contract Conditions</Text>
          <ScrollView style={styles.conditionsScroll}>
            <Text style={styles.conditionsText}>{contractData.conditions}</Text>
          </ScrollView>
        </View>
      ) : null}

      <View style={styles.termsContainer}>
        <TouchableOpacity
          style={styles.termsCheckbox}
          onPress={() => setContractData({ ...contractData, termsAccepted: !contractData.termsAccepted })}
        >
          {contractData.termsAccepted ? (
            <Ionicons name="checkbox" size={24} color="#0e4e96" />
          ) : (
            <Ionicons name="square-outline" size={24} color="#64748B" />
          )}
        </TouchableOpacity>
        <Text style={styles.termsText}>
          I agree to the terms and conditions of this smart contract, and authorize its deployment to the blockchain.
        </Text>
      </View>
    </View>
  );

  // Render project selection modal (full screen)
  const renderProjectModal = () => (
    <Modal
      visible={showProjectModal}
      animationType="slide"
      transparent={false}
      onRequestClose={() => setShowProjectModal(false)}
    >
      <SafeAreaView style={styles.modalFullContainer}>
        <View style={styles.modalFullHeader}>
          <Text style={styles.modalFullTitle}>Select Project</Text>
          <TouchableOpacity onPress={() => setShowProjectModal(false)}>
            <Ionicons name="close" size={28} color="#0e4e96" />
          </TouchableOpacity>
        </View>

        <View style={styles.modalSearchContainer}>
          <Ionicons name="search" size={20} color="#64748B" style={styles.modalSearchIcon} />
          <TextInput
            style={styles.modalSearchInput}
            placeholder="Search projects..."
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>

        {fetchingData ? (
          <ActivityIndicator size="large" color="#0e4e96" style={{ margin: 20 }} />
        ) : (
          <ScrollView style={styles.modalList}>
            {Array.isArray(projects) && projects.length > 0 ? (
              projects
                .filter(project =>
                  project.name &&
                  String(project.name ?? '').toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map(project => (
                  <TouchableOpacity
                    key={project.id}
                    style={styles.modalItem}
                    onPress={() => handleSelectProject(project)}
                  >
                    <View>
                      <Text style={styles.modalItemTitle}>{project.name}</Text>
                      <Text style={styles.modalItemSubtitle}>
                        {project.location || 'No location'}
                      </Text>
                    </View>
                    <Text style={styles.modalItemValue}>
                      {formatCurrency(project.budget || 0)}
                    </Text>
                  </TouchableOpacity>
                ))
            ) : (
              <Text style={styles.emptyStateText}>No projects available</Text>
            )}
          </ScrollView>
        )}
      </SafeAreaView>
    </Modal>
  );

  // Render subcontractor selection modal (full screen) with refresh – FIXED FIELD NAMES
  const renderSubcontractorModal = () => (
    <Modal
      visible={showSubcontractorModal}
      animationType="slide"
      transparent={false}
      onRequestClose={() => setShowSubcontractorModal(false)}
    >
      <SafeAreaView style={styles.modalFullContainer}>
        <View style={styles.modalFullHeader}>
          <Text style={styles.modalFullTitle}>Select Subcontractor</Text>
          <View style={{ flexDirection: 'row' }}>
            <TouchableOpacity onPress={refreshVendors} style={{ marginRight: 16 }}>
              <Ionicons name="refresh" size={24} color="#0e4e96" />
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setShowSubcontractorModal(false)}>
              <Ionicons name="close" size={28} color="#0e4e96" />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.modalSearchContainer}>
          <Ionicons name="search" size={20} color="#64748B" style={styles.modalSearchIcon} />
          <TextInput
            style={styles.modalSearchInput}
            placeholder="Search subcontractors..."
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>

        {refreshingVendors ? (
          <ActivityIndicator size="large" color="#0e4e96" style={{ margin: 20 }} />
        ) : (
          <ScrollView
            style={styles.modalList}
            refreshControl={
              <RefreshControl refreshing={refreshingVendors} onRefresh={refreshVendors} />
            }
          >
            {Array.isArray(subcontractors) && subcontractors.length > 0 ? (
              subcontractors
                .filter(sub => {
                  // Search by name or specialty (case-insensitive)
                  const nameMatch = sub.name && String(sub.name ?? '').toLowerCase().includes(searchQuery.toLowerCase());
                  const specialtyMatch = sub.specialty && String(sub.specialty ?? '').toLowerCase().includes(searchQuery.toLowerCase());
                  return nameMatch || specialtyMatch;
                })
                .map(subcontractor => (
                  <TouchableOpacity
                    key={subcontractor.id}
                    style={styles.modalItem}
                    onPress={() => handleSelectSubcontractor(subcontractor)}
                  >
                    <View>
                      <Text style={styles.modalItemTitle}>{subcontractor.name}</Text>
                      <Text style={styles.modalItemSubtitle}>
                        {subcontractor.specialty}
                      </Text>
                    </View>
                    <View style={styles.ratingContainer}>
                      <Ionicons name="star" size={16} color="#FBBF24" />
                      <Text style={styles.ratingText}>
                        {subcontractor.rating}
                      </Text>
                    </View>
                  </TouchableOpacity>
                ))
            ) : (
              <View style={{ padding: 20, alignItems: 'center' }}>
                <Text style={styles.emptyStateText}>No subcontractors available</Text>
                <TouchableOpacity onPress={refreshVendors} style={{ marginTop: 10 }}>
                  <Text style={{ color: '#0e4e96' }}>Tap to refresh</Text>
                </TouchableOpacity>
              </View>
            )}
          </ScrollView>
        )}
      </SafeAreaView>
    </Modal>
  );

  // Render role picker modal
  const renderRolePickerModal = () => (
    <Modal
      visible={showRolePicker}
      animationType="slide"
      transparent={false}
      onRequestClose={() => setShowRolePicker(false)}
    >
      <SafeAreaView style={styles.modalFullContainer}>
        <View style={styles.modalFullHeader}>
          <Text style={styles.modalFullTitle}>Enter {roleField} name</Text>
          <TouchableOpacity onPress={() => setShowRolePicker(false)}>
            <Ionicons name="close" size={28} color="#0e4e96" />
          </TouchableOpacity>
        </View>

        <View style={{ padding: 20 }}>
          <TextInput
            style={styles.textInput}
            placeholder={`Enter ${roleField} name`}
            value={contractData.roles[roleField]}
            onChangeText={(text) => {
              setContractData({
                ...contractData,
                roles: { ...contractData.roles, [roleField]: text },
              });
            }}
          />
          <TouchableOpacity
            style={[styles.navButton, styles.primaryButton, { marginTop: 20 }]}
            onPress={() => setShowRolePicker(false)}
          >
            <Text style={[styles.navButtonText, styles.primaryButtonText]}>Done</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handlePreviousStep}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        {logoUrl ? (
          <Image source={{ uri: (logoUrl && logoUrl.startsWith('http')) ? logoUrl : undefined }} style={styles.headerLogo} />
        ) : (
          <MaterialCommunityIcons name="file-sign" size={30} color="#fff" />
        )}
        <Text style={styles.headerTitle}>Create New Contract</Text>
        <View style={{ width: 30 }} />
      </View>

      {renderStepIndicators()}

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.formWrapper}
      >
        <ScrollView style={styles.scrollView}>
          {fetchingData ? (
            <ActivityIndicator size="large" color="#0e4e96" style={{ margin: 20 }} />
          ) : (
            <>
              {currentStep === 1 && renderBasicDetailsForm()}
              {currentStep === 2 && renderMilestonesForm()}
              {currentStep === 3 && renderPaymentTermsAndReview()}
            </>
          )}
        </ScrollView>
      </KeyboardAvoidingView>

      <View style={styles.bottomNav}>
        <TouchableOpacity style={styles.navButton} onPress={handlePreviousStep}>
          <Text style={styles.navButtonText}>
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.navButton, styles.primaryButton]}
          onPress={handleNextStep}
          disabled={isLoading || fetchingData}
        >
          {isLoading ? (
            <ActivityIndicator color="#fff" size={20} />
          ) : (
            <Text style={[styles.navButtonText, styles.primaryButtonText]}>
              <Text>{currentStep < 3 ? 'Next' : 'Create Contract'}</Text>
            </Text>
          )}
        </TouchableOpacity>
      </View>

      {showDatePicker && (
        <DateTimePicker
          value={
            activeDateField === 'startDate'
              ? contractData.startDate
              : activeDateField === 'endDate'
              ? contractData.endDate
              : activeDateField.startsWith('milestone-')
              ? contractData.milestones[parseInt(activeDateField.split('-')[1])].dueDate
              : new Date()
          }
          mode={datePickerMode}
          display="default"
          onChange={handleDateChange}
        />
      )}

      {renderProjectModal()}
      {renderSubcontractorModal()}
      {renderRolePickerModal()}
    </SafeAreaView>
  );
};

// ==================== STYLES ====================
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  header: {
    backgroundColor: '#4A90E2',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 16,
  },
  backButton: {
    padding: 4,
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    resizeMode: 'cover',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
  },
  stepIndicatorContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  stepIndicatorWrapper: {
    alignItems: 'center',
  },
  stepIndicator: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  activeStepIndicator: {
    backgroundColor: '#0e4e96',
  },
  completedStepIndicator: {
    backgroundColor: '#10B981',
  },
  stepText: {
    color: '#64748B',
    fontWeight: 'bold',
  },
  activeStepText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  stepLabel: {
    color: '#64748B',
    fontSize: 12,
  },
  formWrapper: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  formContainer: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0F172A',
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0e4e96',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  addButtonText: {
    color: '#fff',
    marginLeft: 4,
    fontSize: 14,
  },
  inputLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 6,
  },
  textInput: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    marginBottom: 16,
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  selectionInput: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    marginBottom: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  inputText: {
    fontSize: 16,
    color: '#0F172A',
  },
  placeholderText: {
    fontSize: 16,
    color: '#94A3B8',
  },
  dateContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  dateField: {
    width: '48%',
  },
  dateInput: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dateText: {
    fontSize: 16,
    color: '#0F172A',
  },
  milestoneCard: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  milestoneHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  milestoneNumber: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  removeButton: {
    padding: 4,
  },
  milestoneSummary: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  milestoneSummaryText: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  matchingAmount: {
    color: '#10B981',
  },
  mismatchAmount: {
    color: '#EF4444',
  },
  settingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  settingLabel: {
    fontSize: 16,
    color: '#0F172A',
  },
  settingValue: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  settingText: {
    fontSize: 16,
    color: '#64748B',
    marginRight: 8,
  },
  summaryCard: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  summaryRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  summaryLabel: {
    fontSize: 14,
    color: '#64748B',
    width: '40%',
  },
  summaryValue: {
    fontSize: 14,
    color: '#0F172A',
    fontWeight: '500',
    flex: 1,
  },
  conditionsCard: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    maxHeight: 200,
  },
  conditionsScroll: {
    maxHeight: 150,
  },
  conditionsText: {
    fontSize: 12,
    color: '#334155',
    lineHeight: 18,
  },
  termsContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginVertical: 16,
  },
  termsCheckbox: {
    marginRight: 8,
    marginTop: -2,
  },
  termsText: {
    fontSize: 14,
    color: '#64748B',
    flex: 1,
  },
  bottomNav: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    backgroundColor: '#fff',
  },
  navButton: {
    flex: 1,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButton: {
    backgroundColor: '#0e4e96',
  },
  navButtonText: {
    fontSize: 16,
    color: '#64748B',
    fontWeight: '500',
  },
  primaryButtonText: {
    color: '#fff',
  },
  // Modal styles
  modalFullContainer: {
    flex: 1,
    backgroundColor: '#fff',
  },
  modalFullHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    backgroundColor: '#f8fafc',
  },
  modalFullTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  modalSearchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    margin: 16,
    paddingHorizontal: 12,
  },
  modalSearchIcon: {
    marginRight: 8,
  },
  modalSearchInput: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 16,
  },
  modalList: {
    flex: 1,
    paddingHorizontal: 16,
  },
  modalItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  modalItemTitle: {
    fontSize: 16,
    color: '#0F172A',
    fontWeight: '500',
  },
  modalItemSubtitle: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 2,
  },
  modalItemValue: {
    fontSize: 16,
    color: '#0F172A',
    fontWeight: '500',
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFBEB',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  ratingText: {
    fontSize: 14,
    color: '#92400E',
    marginLeft: 4,
    fontWeight: '500',
  },
  emptyStateText: {
    textAlign: 'center',
    padding: 20,
    color: '#64748B',
    fontSize: 16,
  },
});

export default CreateNewContractScreen;